import { Intent } from "@blueprintjs/core";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    useSetTeamScenarioArchivedMutation,
    useUpdateScenarioStatusMutation
} from "generated/graphql";
import { segmentTrackArchiveScenario } from "in_queue/analytics";
import { useCurrentScenarioMetadata } from "in_queue/contexts/ScenarioPageContext";
import { ScenarioOrigin, ScenarioStatus } from "in_queue/types/scenarioType";
import { useState } from "react";
import {
    ScenarioArchiveAction,
    ScenarioArchiveAlert,
    ScenarioArchiveToaster
} from "./ScenarioArchiveAlert";

interface ArchiveScenarioAlertProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    action: ScenarioArchiveAction;
}

const ArchiveScenarioAlert: React.FC<ArchiveScenarioAlertProps> = ({
    isOpen,
    setIsOpen,
    action
}: ArchiveScenarioAlertProps) => {
    const [updateScenarioStatus] = useUpdateScenarioStatusMutation();
    const [setTeamScenarioArchived] = useSetTeamScenarioArchivedMutation();
    const { teamId } = useUserDataContext();
    const scenario = useCurrentScenarioMetadata();
    const [isAlertLoading, setIsAlertLoading] = useState(false);

    const archiveCustomScenario = async (archived: boolean) => {
        // This handles the case where scenarios were archived before
        // our archived field existed; if someone tries to unarchive
        // one of these, assign a status of RunSucceeded, which is
        // consistent with past behavior.
        const currStatusIsArchived =
            scenario.status === ScenarioStatus.Archived;

        const hasuraResponse = await updateScenarioStatus({
            variables: {
                scenarioId: scenario.scenarioId,
                status: currStatusIsArchived
                    ? ScenarioStatus.RunSucceeded
                    : scenario.status,
                archived,
                lastArchivedAt: archived
                    ? new Date().toISOString()
                    : scenario.lastArchivedAt
            }
        });
        return (hasuraResponse.data?.update_scenarios?.affected_rows ?? 0) > 0;
    };

    const archivePublishedScenario = async (archived: boolean) => {
        const hasuraResponse = await setTeamScenarioArchived({
            variables: { scenarioId: scenario.scenarioId, teamId, archived }
        });
        return Boolean(hasuraResponse.data?.insert_scenario_configurations_one);
    };

    const archiveScenario = async ({
        action
    }: {
        action: ScenarioArchiveAction;
    }) => {
        setIsAlertLoading(true);

        const archived = action === "archive";
        const succeeded =
            scenario.origin === ScenarioOrigin.NiraPublished
                ? await archivePublishedScenario(archived)
                : await archiveCustomScenario(archived);

        if (succeeded) {
            segmentTrackArchiveScenario({
                scenarioId: scenario.scenarioId,
                action,
                scenarioOrigin: scenario.origin
            });
            setIsAlertLoading(false);
            setIsOpen(false);

            ScenarioArchiveToaster.show({
                intent: Intent.SUCCESS,
                message: `Successfully ${
                    action === "archive" ? "archived" : "unarchived"
                } scenario "${scenario.title}"`
            });
        }
    };

    return (
        <ScenarioArchiveAlert
            action={action}
            count={1}
            isOpen={isOpen}
            isLoading={isAlertLoading}
            onCancel={() => setIsOpen(false)}
            onConfirm={() => archiveScenario({ action })}
        />
    );
};

export default ArchiveScenarioAlert;
